import { FC, useEffect, useMemo, useRef, useState } from 'react'

import { ChevronSmallDown, ChevronSmallUp, Reorder16 } from '@makyoapp/makyo-icon/icon'

import { useCompSettings } from '@/hooks/__project__/useCompSettings'
import { useUpdateCompSettingValue } from '@/hooks/useUpdateCompSettings'
import { BuilderComponent, BuilderComponentSetting, BuilderDependOn, FlattenedItem } from '@/shared/builder'
import { debounce } from '@/shared/utils'
import ComponentSettingField from '@/pages/project-v2/layout/Editor/components/ComponentPanel/ComponentSettingField'

interface GroupSortingProps {
  id: string
  value: string[] | any[]
  groupSortingSettings?:
    | Record<
        string,
        {
          dependOn?: BuilderDependOn
          items: BuilderComponentSetting[]
        }
      >
    | undefined
  flattenedSettings?: BuilderComponentSetting[]
  flattenedComponents?: FlattenedItem[]
  comp?: BuilderComponent
  ancestors?: FlattenedItem[]
}

type GroupDef = {
  id: string
  label: string
  items: BuilderComponentSetting[]
  dependOn?: BuilderDependOn
}

const arraysEqual = (a?: string[], b?: string[]) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

export const GroupSorting: FC<GroupSortingProps> = ({
  id,
  value,
  groupSortingSettings,
  flattenedSettings,
  flattenedComponents,
  comp,
  ancestors,
}) => {
  const groupDefs: GroupDef[] = useMemo(() => {
    if (!groupSortingSettings) return []
    return Object.entries(groupSortingSettings).map(([label, def]) => ({
      id: label,
      label,
      items: def.items ?? [],
      dependOn: def.dependOn,
    }))
  }, [groupSortingSettings])

  const settings = useCompSettings(comp as any)

  const evaluateGroupVisible = (dependOn?: BuilderDependOn) => {
    if (!dependOn) return true
    const v = settings?.[dependOn.id]
    try {
      return typeof dependOn.showWhen === 'function' ? !!dependOn.showWhen(v, settings as any, comp as any) : !!v
    } catch {
      return !!v
    }
  }

  const initialOrder = useMemo(() => {
    const ids = groupDefs.map((g) => g.id)
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      return (value as string[])
        .filter((id) => ids.includes(id))
        .concat(ids.filter((id) => !(value as string[]).includes(id)))
    }
    return ids
  }, [groupDefs, value])

  const [order, setOrder] = useState<string[]>(initialOrder)
  useEffect(() => {
    if (!arraysEqual(order, initialOrder)) {
      setOrder(initialOrder)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOrder])

  const updateValue = useUpdateCompSettingValue(id)

  const debouncedUpdateRef = useRef(
    debounce(async (latestOrder: string[]) => {
      try {
        await updateValue(latestOrder as any)
      } catch (error) {
        console.error('Failed to update group order:', error)
      }
    }, 50)
  )

  useEffect(() => {
    const saved = Array.isArray(value) && typeof value[0] === 'string' ? (value as string[]) : []
    if (!arraysEqual(order, saved)) {
      debouncedUpdateRef.current(order)
    }
  }, [order, value])

  const handleDragStart = (e: React.DragEvent, groupId: string) => {
    e.dataTransfer.setData('text/plain', groupId)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    const draggedId = e.dataTransfer.getData('text/plain')
    if (!draggedId || draggedId === targetId) return

    const cur = [...order]
    const from = cur.indexOf(draggedId)
    const to = cur.indexOf(targetId)
    if (from < 0 || to < 0) return

    const [moved] = cur.splice(from, 1)
    cur.splice(to, 0, moved)
    setOrder(cur)
  }

  const [openId, setOpenId] = useState<string | null>(null)

  const groupMap = useMemo(() => {
    const map = new Map<string, GroupDef>()
    for (const g of groupDefs) map.set(g.id, g)
    return map
  }, [groupDefs])

  return (
    <div className='flex flex-col gap-2 w-full'>
      {order.map((groupId) => {
        const g = groupMap.get(groupId)
        if (!g) return null
        // evaluasi visibilitas grup berdasarkan dependOn
        if (!evaluateGroupVisible(g.dependOn)) return null

        const isOpen = openId === g.id
        return (
          <div
            key={g.id}
            className='rounded-sm bg-white dark:bg-gray-700 shadow-xs'
            draggable
            onDragStart={(e) => handleDragStart(e, g.id)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, g.id)}
          >
            <button
              type='button'
              className='w-full flex items-center gap-4 py-3 px-4 cursor-pointer bg-gray-50 dark:bg-transparent hover:bg-gray-80 dark:hover:bg-gray-500'
              onClick={() => setOpenId(isOpen ? null : g.id)}
            >
              <div className='flex-1 flex items-center gap-2'>
                <Reorder16 />
                <h3 className='font-medium'>{g.label}</h3>
              </div>
              {isOpen ? <ChevronSmallUp className='w-3 h-3' /> : <ChevronSmallDown className='w-3 h-3' />}
            </button>

            {isOpen && (
              <div className='p-4 bg-gray-50 dark:bg-gray-700 border-t dark:border-t-gray-500'>
                <div className='flex flex-col gap-2'>
                  {g.items.map((child) => (
                    <ComponentSettingField
                      key={child.id}
                      {...child}
                      id={child.id}
                      flattenedSettings={flattenedSettings ?? []}
                      flattenedComponents={flattenedComponents ?? []}
                      comp={comp as BuilderComponent}
                      ancestors={ancestors ?? ([] as FlattenedItem[])}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
